<template>
  <div class="lucky-referral">
    <!-- Header -->
    <header class="lr-header">
      <button class="lr-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="lr-header__title">{{ $lang.lr_title || 'LUCKY REFERRAL' }}</h1>
      <div class="lr-header__spacer"></div>
    </header>

    <!-- Dashboard Card -->
    <section class="lr-dashboard">
      <!-- Target Reward -->
      <div class="lr-target">
        <p class="lr-target__label">{{ $lang.lr_target_reward }}</p>
        <div class="lr-target__plaque">
          <h2 class="lr-target__amount">{{ getCurrency }} {{ $formatNumberWithCommas(targetReward) }}</h2>
        </div>
      </div>

      <!-- Current Earnings -->
      <div class="lr-earnings">
        <div class="lr-earnings__info">
          <p class="lr-earnings__label">{{ $lang.lr_current_earnings }}</p>
          <span class="lr-earnings__amount">{{ getCurrency }} {{ $formatNumberWithCommas(currentEarnings) }}</span>
        </div>
        <img
          class="lr-earnings__token"
          src="../../../assets/img/otgame/amount.png"
          width="40"
        />
      </div>

      <!-- Progress Bar -->
      <div class="lr-progress">
        <div class="lr-progress__track">
          <div
            class="lr-progress__fill"
            :class="{ 'lr-progress__fill--empty': progressPercent === 0 }"
            :style="{ width: progressPercent + '%' }"
          >
            <div v-if="progressPercent > 0" class="lr-progress__glow"></div>
            <span v-if="progressPercent > 0" class="lr-progress__text">{{ progressPercent }}%</span>
          </div>
          <span v-if="progressPercent === 0" class="lr-progress__text lr-progress__text--center">0%</span>
        </div>
        <p class="lr-progress__hint">
          {{ $lang.lr_spins_hint }}
        </p>
      </div>

      <!-- Claim Button -->
      <button
        class="lr-claim"
        :class="{ 'lr-claim--active': canClaim }"
        :disabled="!canClaim"
        @click="claimReward"
      >
        <van-icon v-if="!canClaim" name="lock" size="18" />
        <span>{{ $lang.lr_claim_reward }}</span>
      </button>
    </section>

    <!-- Wheel Visual -->
    <section class="lr-wheel-section">
      <div class="lr-wheel-container">
        <div class="lr-wheel" :style="{ transform: `rotate(${wheelAngle}deg)` }">
          <img
            src="../../../assets/img/activity/activity_banner/LuckyWheel.png"
            class="lr-wheel__img"
          />
        </div>
        <!-- Inner overlay -->
        <div class="lr-wheel__border"></div>
        <!-- Pointer -->
        <div class="lr-wheel__pointer"></div>
        <!-- Center dot -->
        <div class="lr-wheel__center">
          <div class="lr-wheel__center-dot"></div>
        </div>
      </div>

      <button
        class="lr-spin-btn"
        :disabled="isSpinning"
        @click="spin"
      >
        {{ isSpinning ? $lang.lr_spinning : ($lang.lr_spin_x || 'SPIN') + ' x' + remainingSpins }}
      </button>
    </section>

    <!-- Referral Link -->
    <section class="lr-referral">
      <label class="lr-referral__label">{{ $lang.lr_referral_link }}</label>
      <div class="lr-referral__row">
        <div class="lr-referral__link">{{ referralLink }}</div>
        <button class="lr-referral__copy" @click="copyLink">{{ $lang.lr_copy }}</button>
      </div>
    </section>

    <!-- Rules -->
    <section class="lr-rules">
      <p class="lr-rules__text">
        <span class="lr-rules__highlight">{{ $lang.lr_rules_highlight }}</span>
        {{ $lang.lr_rules_desc }}
      </p>
    </section>

    <!-- Recent Winners -->
    <section class="lr-winners">
      <div class="lr-winners__header">
        <h3 class="lr-winners__title">{{ $lang.lr_recent_winners }}</h3>
        <span class="lr-winners__live">{{ $lang.lr_live_feed || 'Live Feed' }}</span>
      </div>
      <div class="lr-winners__table">
        <div class="lr-winners__thead">
          <span>{{ $lang.lr_player_name }}</span>
          <span>{{ $lang.lr_reward_amount }}</span>
        </div>
        <div class="lr-winners__marquee">
          <div class="lr-winners__marquee-content" ref="marqueeContent">
            <div
              v-for="(winner, idx) in winnersDouble"
              :key="idx"
              class="lr-winners__row"
            >
              <span class="lr-winners__name">{{ winner.name }}</span>
              <span class="lr-winners__amount">{{ getCurrency }} {{ $formatNumberWithCommas(winner.amount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Insufficient Spins Popup -->
    <van-popup
      v-model="showNoSpins"
      round
      :close-on-click-overlay="true"
      class="lr-popup-wrapper"
    >
      <div class="lr-popup">
        <div class="lr-popup__icon">
          <van-icon name="warning-o" size="40" color="#24ee89" />
        </div>
        <h2 class="lr-popup__title">{{ $lang.lr_insufficient_spins }}</h2>
        <p class="lr-popup__desc">{{ $lang.lr_invite_more }}</p>

        <!-- Social Share Icons -->
        <div class="lr-popup__socials">
          <a class="lr-popup__social lr-popup__social--fb" @click="shareToSocial('x')">
            <img src="../../../assets/img/otgame/pinduoduo_24.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--tg" @click="shareToSocial('facebook')">
            <img src="../../../assets/img/otgame/zjm_39.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--wa" @click="shareToSocial('youtube')">
            <img src="../../../assets/img/otgame/zjm_42.png" width="24" />
          </a>
          <a class="lr-popup__social lr-popup__social--x" @click="shareToSocial('ins')">
            <img src="../../../assets/img/otgame/ins.png" width="24" />
          </a>
        </div>

        <!-- Referral Link in popup -->
        <div class="lr-popup__ref">
          <p class="lr-popup__ref-label">{{ $lang.lr_referral_link }}</p>
          <div class="lr-popup__ref-row">
            <div class="lr-popup__ref-link">{{ referralLink }}</div>
            <button class="lr-popup__ref-copy" @click="copyLink">{{ $lang.lr_copy }}</button>
          </div>
        </div>

        <!-- Close Button -->
        <button class="lr-popup__close" @click="showNoSpins = false">
          {{ $lang.lr_close }}
        </button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import {
  GetLuckyRoulette,
  LuckyRoulette,
  ReceiveLuckyRouletteBonus,
  GetLuckyRouletteLatestAwards
} from '@/api/common'
import { reportPromoPanel } from '@/utils/common'

export default {
  name: 'LuckyReferral',
  data() {
    return {
      // API data
      targetReward: 0,
      currentEarnings: 0,
      remainingSpins: 0,
      invitedCount: 0,
      isOpen: 0,
      receiveStatus: 0, // 0=抽奖中 1=等待领取 2=已领取 3=已过期
      freeTimes: 0,
      minInviteCountToReward: 0,
      // UI state
      isSpinning: false,
      wheelAngle: 0,
      showNoSpins: false,
      winners: []
    }
  },
  computed: {
    progressPercent() {
      if (!this.targetReward) return 0
      return Math.min(
        Math.round((this.currentEarnings / this.targetReward) * 1000) / 10,
        100
      )
    },
    canClaim() {
      return this.receiveStatus === 1
    },
    spinsToTarget() {
      if (!this.targetReward) return 0
      const remaining = this.targetReward - this.currentEarnings
      return remaining > 0 ? Math.ceil(remaining / 100) : 0
    },
    referralLink() {
      const code = localStorage.getItem('shareCode') || ''
      return `${window.location.host}/${this.GET_COUNTRY}/home?id=${code}`
    },
    winnersDouble() {
      return [...this.winners, ...this.winners]
    }
  },
  mounted() {
    reportPromoPanel('lucky_roulette', 1)
    this.fetchData()
    this.fetchWinners()
  },
  beforeDestroy() {
    reportPromoPanel('lucky_roulette', 2)
  },
  methods: {
    async fetchData() {
      try {
        const res = await GetLuckyRoulette({})
        if (res && res.content) {
          const d = res.content
          this.currentEarnings = d.bonusSum || 0
          this.remainingSpins = d.drawCount || 0
          this.invitedCount = d.invitedCount || 0
          this.isOpen = d.isOpen || 0
          this.receiveStatus = d.receiveStatus || 0
          if (d.rules) {
            this.targetReward = d.rules.bonusAmount || 0
            this.freeTimes = d.rules.freeTimes || 0
            this.minInviteCountToReward = d.rules.minInviteCountToReward || 0
          }
        }
      } catch (e) {
        console.log('fetchData error', e)
      }
    },
    async fetchWinners() {
      try {
        const res = await GetLuckyRouletteLatestAwards({})
        if (res && res.content && res.content.list) {
          this.winners = res.content.list.map(item => ({
            name: item.userAccount || '',
            amount: item.amount || 0
          }))
        }
      } catch (e) {
        console.log('fetchWinners error', e)
      }
    },
    async spin() {
      if (this.isSpinning) return
      if (this.remainingSpins <= 0) {
        this.showNoSpins = true
        return
      }
      this.isSpinning = true
      const extraDeg = 360 * 5 + Math.floor(Math.random() * 360)
      this.wheelAngle += extraDeg

      try {
        const res = await LuckyRoulette({})
        // Wait for wheel animation to finish
        setTimeout(() => {
          this.isSpinning = false
          if (res && res.content) {
            const d = res.content
            const prevEarnings = this.currentEarnings
            this.currentEarnings = d.bonusSum || 0
            this.remainingSpins = d.drawCount || 0
            this.receiveStatus = d.receiveStatus || 0
            this.invitedCount = d.invitedCount || 0
            const earned = this.currentEarnings - prevEarnings
            if (earned > 0) {
              this.$toast({
                message: `+${this.getCurrency} ${this.$formatNumberWithCommas(earned)}`,
                icon: 'success'
              })
            }
          }
        }, 3000)
      } catch (e) {
        console.log('spin error', e)
        setTimeout(() => {
          this.isSpinning = false
        }, 3000)
      }
    },
    async claimReward() {
      if (!this.canClaim) return
      try {
        const res = await ReceiveLuckyRouletteBonus({})
        if (res && res.content) {
          const d = res.content
          this.receiveStatus = d.receiveStatus || 0
          this.currentEarnings = d.bonusSum || 0
          this.remainingSpins = d.drawCount || 0
          this.$toast({
            message: this.$lang.Sucesso || 'Success',
            icon: 'success'
          })
        }
      } catch (e) {
        console.log('claimReward error', e)
      }
    },
    copyLink() {
      const textarea = document.createElement('textarea')
      textarea.value = this.referralLink
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$toast({
        message: this.$lang.Sucesso || 'Success',
        icon: 'success'
      })
    },
    shareToSocial(platform) {
      const links = {
        facebook: `https://www.facebook.com`,
        ins: `https://instagram.com`,
        x: `https://x.com`,
        youtube: `https://youtube.com`,
      }
      if (links[platform]) {
        window.open(links[platform], '_blank')
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: #24ee89;
@gold: #f8f510;
@bg-dark: #0a0e1a;
@card-bg: #161b2b;
@red: #dc2626;

.lucky-referral {
  min-height: 100vh;
  background: @bg-dark;
  padding-bottom: 30px;
}

// ====== HEADER ======
.lr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: @bg-dark;

  &__back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #ffffff;
  }

  &__spacer {
    width: 40px;
  }
}

// ====== DASHBOARD ======
.lr-dashboard {
  background: @card-bg;
  padding: 20px;
  border-radius: 10px;
  margin: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6), 0 0 20px rgba(36, 238, 137, 0.03);
}

// Target Reward
.lr-target {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;

  &__label {
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    color: #9ca3af;
    margin-bottom: 8px;
  }

  &__plaque {
    background: linear-gradient(145deg, #fffb80 0%, @gold 30%, #ca8a04 60%, #854d0e 100%);
    border: 1px solid #fef08a;
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.9),
      inset 0 -4px 15px rgba(0, 0, 0, 0.6),
      0 15px 35px rgba(0, 0, 0, 0.8);
    border-radius: 8px;
    padding: 12px 32px;
    width: 100%;
    max-width: 280px;
    text-align: center;
    position: relative;
    overflow: hidden;
    animation: plaque-glow 4s infinite ease-in-out;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(110deg, transparent 40%, rgba(255, 255, 255, 0.6) 50%, transparent 60%);
      animation: shine-plaque 4s infinite linear;
    }
  }

  &__amount {
    font-size: 48px;
    font-weight: 900;
    color: #000000;
    letter-spacing: -2px;
    text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.4);
    position: relative;
    z-index: 1;
  }
}

@keyframes plaque-glow {
  0% { box-shadow: 0 0 15px rgba(248, 245, 16, 0.3), 0 15px 30px rgba(0, 0, 0, 0.7); }
  50% { box-shadow: 0 0 35px rgba(248, 245, 16, 0.6), 0 15px 30px rgba(0, 0, 0, 0.7); }
  100% { box-shadow: 0 0 15px rgba(248, 245, 16, 0.3), 0 15px 30px rgba(0, 0, 0, 0.7); }
}

@keyframes shine-plaque {
  0% { transform: translateX(-150%); }
  100% { transform: translateX(150%); }
}

// Earnings
.lr-earnings {
  background: #0d121f;
  box-shadow:
    inset 0 4px 12px rgba(0, 0, 0, 0.9),
    inset 0 1px 2px rgba(255, 255, 255, 0.05),
    0 1px 0px rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.4);
  padding: 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #6b7280;
  }

  &__amount {
    font-size: 24px;
    font-weight: 900;
    color: #ffffff;
    letter-spacing: -0.5px;
  }

  &__token {
    filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.8)) brightness(1.1);
  }
}

// Progress
.lr-progress {
  margin-bottom: 16px;

  &__track {
    background: #05070c;
    border: 1.5px solid rgba(255, 255, 255, 0.15);
    border-radius: 50px;
    box-shadow:
      inset 0 8px 12px rgba(0, 0, 0, 0.95),
      0 4px 15px rgba(0, 0, 0, 0.8);
    height: 34px;
    position: relative;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background:
      repeating-linear-gradient(45deg, rgba(204, 47, 25, 0.1) 0px, rgba(204, 47, 25, 0.1) 10px, transparent 10px, transparent 20px),
      linear-gradient(90deg, #8b1e10 0%, @red 50%, #8b1e10 100%);
    background-size: 40px 40px, 200% 100%;
    animation: energy-swirl 1.5s linear infinite;
    border-radius: 50px 0 0 50px;
    position: relative;
    box-shadow: inset 0 2px 8px rgba(255, 255, 255, 0.4), 0 0 25px rgba(220, 38, 38, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 50px;

    &--empty {
      background: transparent;
      animation: none;
      box-shadow: none;
      min-width: 0;
    }
  }

  &__glow {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 10px;
    background: white;
    filter: blur(3px);
    box-shadow:
      0 0 25px 6px @red,
      0 0 45px 12px rgba(255, 255, 255, 0.8);
    animation: pulsing-discharge 0.8s ease-in-out infinite alternate;
    z-index: 4;
  }

  &__text {
    font-size: 12px;
    font-weight: 900;
    color: #ffffff;
    z-index: 10;
    position: relative;

    &--center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &__hint {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: @primary;
    margin-top: 10px;
    animation: pulse-text 2s ease-in-out infinite;
    text-shadow: 0 0 8px rgba(36, 238, 137, 0.4);
  }
}

@keyframes energy-swirl {
  0% { background-position: 0 0, 100% 0; }
  100% { background-position: 40px 0, 0% 0; }
}

@keyframes pulsing-discharge {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

// Claim Button
.lr-claim {
  width: 100%;
  max-width: 220px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  border: 1px solid rgba(36, 238, 137, 0.2);
  background: rgba(15, 23, 42, 0.5);
  color: #ffffff;
  cursor: not-allowed;

  &--active {
    background: @primary;
    color: #000000;
    border: none;
    cursor: pointer;
    box-shadow: 0 0 20px rgba(36, 238, 137, 0.6);

    &:active {
      transform: scale(0.95);
    }
  }
}

// ====== WHEEL ======
.lr-wheel-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 8px;
}

.lr-wheel-container {
  position: relative;
  width: 280px;
  height: 280px;
  perspective: 1500px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(36, 238, 137, 0.15) 0%, transparent 70%);
    z-index: -1;
    pointer-events: none;
  }
}

.lr-wheel {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: #1a1a2e;
  border: 6px solid #2f323d;
  box-shadow:
    inset 0 0 50px rgba(0, 0, 0, 0.8),
    0 25px 50px -12px rgba(0, 0, 0, 0.7),
    0 0 20px rgba(36, 238, 137, 0.1);
  transition: transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99);

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__border {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 4px solid rgba(255, 255, 255, 0.1);
    pointer-events: none;
  }

  &__pointer {
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-top: 20px solid @gold;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
    z-index: 10;
  }

  &__center {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  }

  &__center-dot {
    width: 28px;
    height: 28px;
    background: @primary;
    border-radius: 50%;
    box-shadow: 0 0 15px @primary;
  }
}

.lr-spin-btn {
  margin-top: 24px;
  width: 100%;
  max-width: 180px;
  background: @primary;
  color: @bg-dark;
  font-weight: 900;
  font-size: 16px;
  padding: 10px;
  border-radius: 10px;
  border: none;
  text-transform: uppercase;
  letter-spacing: 3px;
  box-shadow: 0 0 20px rgba(36, 238, 137, 0.6), 0 0 40px rgba(36, 238, 137, 0.2);
  cursor: pointer;

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// ====== REFERRAL ======
.lr-referral {
  padding: 0 16px;
  margin-top: 24px;

  &__label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 3px;
    color: #6b7280;
    padding-left: 4px;
    margin-bottom: 8px;
    display: block;
  }

  &__row {
    display: flex;
    gap: 8px;
  }

  &__link {
    flex: 1;
    background: @card-bg;
    border: 1px solid rgba(107, 114, 128, 0.3);
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 12px;
    color: #d1d5db;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__copy {
    background: @red;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    padding: 12px 20px;
    border-radius: 10px;
    border: none;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }
  }
}

// ====== RULES ======
.lr-rules {
  margin: 16px 16px 0;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid @primary;

  &__text {
    font-size: 12px;
    line-height: 1.6;
    color: #9ca3af;
  }

  &__highlight {
    color: #ffffff;
    font-weight: 700;
  }
}

// ====== WINNERS ======
.lr-winners {
  padding: 16px;
  margin-top: 8px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px;
    margin-bottom: 12px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #ffffff;
  }

  &__live {
    font-size: 10px;
    background: rgba(107, 114, 128, 0.3);
    padding: 4px 8px;
    border-radius: 4px;
    color: #9ca3af;
  }

  &__table {
    background: @card-bg;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(107, 114, 128, 0.2);
  }

  &__thead {
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.3);
    font-size: 10px;
    text-transform: uppercase;
    color: #6b7280;
    font-weight: 600;
  }

  &__marquee {
    height: 180px;
    overflow: hidden;
    position: relative;
  }

  &__marquee-content {
    display: flex;
    flex-direction: column;
    animation: marquee-vertical 15s linear infinite;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(31, 41, 55, 0.5);
    font-size: 12px;
  }

  &__name {
    font-weight: 500;
    color: #e5e7eb;
  }

  &__amount {
    color: @gold;
    font-weight: 700;
  }
}

@keyframes marquee-vertical {
  0% { transform: translateY(0); }
  100% { transform: translateY(-50%); }
}

// ====== POPUP ======
.lr-popup-wrapper {
  background: transparent !important;
  overflow: visible !important;
}

.lr-popup {
  width: calc(100vw - 40px);
  max-width: 360px;
  box-sizing: border-box;
  background: rgba(10, 14, 26, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(36, 238, 137, 0.2);
  box-shadow: 0 0 20px rgba(36, 238, 137, 0.15);
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  &__icon {
    width: 64px;
    height: 64px;
    background: rgba(36, 238, 137, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    color: #ffffff;
  }

  &__desc {
    font-size: 14px;
    color: #94a3b8;
    text-align: center;
    padding: 0 16px;
  }

  &__socials {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 8px;
  }

  &__social {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

    &--fb { background: #1877f2; }
    &--tg { background: #0088cc; }
    &--wa { background: #25d366; }
    &--x { background: #000000; border: 1px solid rgba(255, 255, 255, 0.2); }
  }

  &__ref {
    width: 100%;

    &-label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #6b7280;
      margin-bottom: 8px;
      padding-left: 4px;
    }

    &-row {
      display: flex;
      align-items: center;
      background: #05070a;
      border-radius: 10px;
      padding: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      min-width: 0;
    }

    &-link {
      flex: 1;
      min-width: 0;
      padding: 8px 12px;
      color: #d1d5db;
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-copy {
      flex-shrink: 0;
    }

    &-copy {
      background: @red;
      color: #ffffff;
      font-size: 12px;
      font-weight: 700;
      padding: 8px 24px;
      border-radius: 8px;
      border: none;
      cursor: pointer;

      &:active {
        opacity: 0.8;
      }
    }
  }

  &__close {
    width: 100%;
    background: @primary;
    color: #000000;
    font-weight: 900;
    padding: 16px;
    border-radius: 10px;
    font-size: 18px;
    border: none;
    cursor: pointer;
    box-shadow: 0 0 20px rgba(36, 238, 137, 0.4);

    &:active {
      transform: scale(0.98);
    }
  }
}

// ====== RESPONSIVE ======
@media (min-width: 769px) {
  .lucky-referral {
    max-width: 450px;
    margin: 0 auto;
  }
}
</style>
